import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useRoutes } from "react-router-dom";
import siteRoutes from "src/features/site/routes";
import commonRoutes from "./common-routes";
import LaunchErrorOverlay from "./components/atoms/launch-error-overlay";
import Loader from "./components/atoms/loader/loader";
import AdminOrganizationsErrorContent from "./components/atoms/organization/admin-organizations-error-content";
import { OnboardingDialog } from "./components/organisms/onboarding/onboarding-dialog";
import {
  determineIfOnboardingDialogShouldBeOpen,
  determineOnboardingContextByRegistrationType,
} from "./components/organisms/onboarding/utils";
import { LAKESIDE_SITE_TOKEN } from "./constants";
import { authRoutes } from "./constants/auth-routes";
import { useAuth } from "./contexts/auth-provider";
import { useAvailableSecurityAdminsSites } from "./features/security/hooks/api/use-available-security-admins-sites";
import securityRoutes from "./features/security/routes";
import { useSites } from "./features/site-details/hooks/api/use-sites";
import { useSiteEvChargersModuleAccess } from "./features/site-ev-chargers/hooks/api/use-site-ev-chargers-module-access";
import { useSiteAdminsProfile } from "./features/site-management/hooks/api/admins/use-site-admins-profile";
import { useTenantEvChargersModuleAccess } from "./features/tenant-ev-chargers/hooks/api/use-tenant-ev-chargers-module-access";
import tenantRoutes from "./features/tenant/routes";
import { useAdminOrganizations } from "./hooks/use-admin-organizations";
import { useDialog } from "./hooks/use-dialog";
import { useSetInitialOrganizations } from "./hooks/use-set-initial-organizations";
import { useSetViewContext } from "./hooks/use-set-view-context";
import useStore from "./hooks/use-store";
import { useUpdateLanguage } from "./hooks/use-update-language";
import { AbilityProvider } from "./permissions/can";
import { ViewContext } from "./store/user-store";
import { UserRole } from "./types/enum/user-role";
import { useMeilisearchApiKey } from "./hooks/use-meilisearch-api-key";

const getRoutesForViewContext = (currentViewContext: ViewContext) => {
  switch (currentViewContext) {
    case "site":
      return [...siteRoutes, ...commonRoutes];

    case "tenant":
      return [...tenantRoutes, ...commonRoutes];

    case "security":
      return [...securityRoutes, ...commonRoutes];
  }
};

const App = () => {
  const { user } = useStore();

  const location = useLocation();
  const { t } = useTranslation();

  const content = useRoutes(getRoutesForViewContext(user.viewContext));

  const auth = useAuth();

  const isLoggedIn = !!auth.session?.access_token;

  const shouldEnableOnboardingDialogCheck = !authRoutes.includes(location.pathname);

  const enableDataFetching = isLoggedIn && shouldEnableOnboardingDialogCheck;

  const [isOnboardingDialogOpen, handleOpenOnboardingDialog, handleCloseOnboardingDialog] =
    useDialog();

  const [{ data, isLoading: isLoadingAdminOrganizations, error: adminOrganizationsError }] =
    useAdminOrganizations(enableDataFetching);

  const sitesQuery = useSites(undefined, enableDataFetching);

  const selectedOrganizationIsInLakeside = useMemo(() => {
    if (!sitesQuery.data) {
      return false;
    }

    const organizationIsInLakesideSite =
      sitesQuery.data?.some(site => site.token === LAKESIDE_SITE_TOKEN) ?? false;

    return organizationIsInLakesideSite;
  }, [sitesQuery.data]);

  const {
    data: siteAdminData,
    isLoading: isLoadingSiteAdminProfile,
    error: siteAdminProfileError,
  } = useSiteAdminsProfile(enableDataFetching, false);

  const {
    data: securityAdminsSites,
    isLoading: isLoadingSecurityAdminsSites,
    error: securityAdminsSitesError,
  } = useAvailableSecurityAdminsSites(enableDataFetching, false);

  //? TODO: disable billing data fetching because Salesforce throws errors because it is not paid
  // const { data: billingData } = useBillings(undefined, isLoggedIn && !!data);

  useSetViewContext();

  useSetInitialOrganizations(enableDataFetching);

  const { data: siteEvChargersModuleAccess } = useSiteEvChargersModuleAccess();
  const { data: tenantEvChargersModuleAccess } = useTenantEvChargersModuleAccess();

  useMeilisearchApiKey(isLoggedIn && !isLoadingAdminOrganizations);

  useEffect(() => {
    if (
      determineIfOnboardingDialogShouldBeOpen(
        data,
        siteAdminData,
        shouldEnableOnboardingDialogCheck,
      )
    ) {
      handleOpenOnboardingDialog();
    }
  }, [data, siteAdminData, securityAdminsSites, user.viewContext, location.pathname]);

  // TODO?: do we need this??
  //TODO REFACTOR FETCHING AND STORING GROUPS
  // GroupStore is outdated and can be removed?
  // useEffect(() => {
  //   if (user.userOrganizationId && enableDataFetching) {
  //     groups.fetchGroups();
  //   }
  // }, [isLoggedIn, enableDataFetching]);

  useUpdateLanguage();

  const renderContent = () => {
    if (
      auth.loading ||
      (enableDataFetching && user.isLoadingViewContextAccess) ||
      (enableDataFetching && isLoadingAdminOrganizations && siteAdminProfileError) ||
      (enableDataFetching && isLoadingSiteAdminProfile && adminOrganizationsError) ||
      (enableDataFetching && isLoadingSecurityAdminsSites && securityAdminsSitesError)
    ) {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <Loader />
        </Box>
      );
    }

    if (
      adminOrganizationsError &&
      siteAdminProfileError &&
      securityAdminsSitesError &&
      enableDataFetching
    ) {
      return (
        <LaunchErrorOverlay
          title={t("siteManagement:siteAdmins.launchError.title")}
          trailingContentContainer={<AdminOrganizationsErrorContent />}
        />
      );
    }

    if (
      determineIfOnboardingDialogShouldBeOpen(
        data,
        siteAdminData,
        shouldEnableOnboardingDialogCheck,
      )
    ) {
      return (
        <OnboardingDialog
          isOpen={isOnboardingDialogOpen}
          onClose={handleCloseOnboardingDialog}
          role={determineOnboardingContextByRegistrationType(data, siteAdminData)}
        />
      );
    } else {
      return content;
    }
  };

  return (
    <AbilityProvider
      viewContext={user.viewContext}
      role={user.viewContext === "site" ? siteAdminData?.role : (data?.role ?? UserRole.VIEWER)}
      userEmail={user.viewContext === "site" ? siteAdminData?.email : (data?.email ?? "")}
      userToken={user.viewContext === "site" ? (siteAdminData?.token ?? "") : (data?.token ?? "")}
      organizationPlan={data?.actualOrganization.plan ?? "free"}
      shouldHaveAccessToSiteActionsInTenantContext={
        data?.actualOrganization?.shouldHaveAccessToSiteActionsInTenantContext
      }
      isLegacyAgreement={
        user.viewContext === "site"
          ? siteAdminData?.actualSite?.isLegacyAgreement
          : data?.actualOrganization?.isLegacyAgreement
      }
      //? TODO: disable billing data fetching because Salesforce throws errors because it is not paid
      // hasAccessControlIntegration={
      //   billingData?.licenseForParkingSpots?.hasAccessControlIntegration
      // }
      hasAccessControlIntegration={false}
      siteHasAccessToEVChargers={siteEvChargersModuleAccess?.hasAccessToEVChargers}
      tenantHasAccessToEVChargers={tenantEvChargersModuleAccess?.hasAccessToEVChargers}
      isLakesideSite={selectedOrganizationIsInLakeside}
      // Allow master to do actions blocked for sso admins
      isSSOUser={data?.role === UserRole.MASTER ? false : data?.actualOrganization?.ssoEnabled}>
      {renderContent()}
    </AbilityProvider>
  );
};

export default observer(App);
